import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Star, Calendar, TrendingUp, Film } from 'lucide-react'
import api from '../../services/api'

interface NewMoviesWidgetProps {
  widget: any
  isExpanded: boolean
}

interface Movie {
  id: number
  title: string
  poster: string
  backdrop: string
  overview: string
  rating: number
  releaseDate: string
  genre: string[]
  trending: boolean
}

export default function NewMoviesWidget({ widget, isExpanded }: NewMoviesWidgetProps) {
  const [category, setCategory] = useState<'now_playing' | 'upcoming' | 'trending'>('now_playing')
  const [selectedMovie, setSelectedMovie] = useState<Movie | null>(null)
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set())

  // Fetch movies from API with 6-hour caching
  const { data: moviesData = { now_playing: [], upcoming: [], trending: [] } } = useQuery({
    queryKey: ['movies'],
    queryFn: async () => {
      const { data } = await api.get('/movies/all')
      return data
    },
    staleTime: 1000 * 60 * 60 * 6, // 6 hours
  })

  const handleImageError = (src: string) => {
    setImageErrors(prev => new Set([...prev, src]))
  }

  const getFallbackImage = (type: 'poster' | 'backdrop') => {
    if (type === 'poster') {
      return 'https://via.placeholder.com/300x450/667eea/ffffff?text=No+Image'
    }
    return 'https://via.placeholder.com/1280x720/667eea/ffffff?text=No+Image'
  }

  const handleWatchTrailer = (movie: Movie) => {
    // Search for the movie trailer on YouTube
    const searchQuery = encodeURIComponent(`${movie.title} ${new Date(movie.releaseDate).getFullYear()} official trailer`)
    window.open(`https://www.youtube.com/results?search_query=${searchQuery}`, '_blank')
  }

  const movies = moviesData[category] || []

  const renderCompactView = () => (
    <div className="h-full overflow-y-auto space-y-2 p-4">
      {movies.map((movie) => (
        <div
          key={movie.id}
          className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded-lg transition cursor-pointer"
          onClick={() => {
            if (isExpanded) {
              setSelectedMovie(movie)
            } else {
              // In compact view, show a helpful message
              toast.info('Click the expand button (⤢) to view movie details', {
                duration: 3000,
                position: 'bottom-center'
              })
            }
          }}
          style={{ backgroundColor: 'transparent' }}
        >
          <img
            src={imageErrors.has(movie.poster) ? getFallbackImage('poster') : movie.poster}
            alt={movie.title}
            className="w-16 h-24 object-cover rounded shadow flex-shrink-0"
            onError={() => handleImageError(movie.poster)}
          />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm mb-1 line-clamp-2" style={{ color: 'var(--color-text-primary)' }}>{movie.title}</div>
            <div className="flex items-center gap-2 mb-1">
              <div className="flex items-center gap-1">
                <Star size={12} className="text-yellow-500 fill-current" />
                <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{movie.rating}</span>
              </div>
              {movie.trending && (
                <div className="flex items-center gap-1 text-red-600">
                  <TrendingUp size={12} />
                  <span className="text-xs font-medium">Trending</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              <Calendar size={10} />
              {new Date(movie.releaseDate).toLocaleDateString()}
            </div>
          </div>
        </div>
      ))}
      
      {movies.length === 0 && (
        <div className="text-center py-8" style={{ color: 'var(--color-text-secondary)' }}>
          <Film size={48} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">No movies in this category</p>
        </div>
      )}
    </div>
  )

  const renderExpandedView = () => (
    <div className="h-full flex gap-4 p-4">
      {/* Movies Grid */}
      <div className="flex-1 overflow-y-auto">
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setCategory('now_playing')}
            className={`px-4 py-2 rounded-lg text-sm transition ${
              category === 'now_playing'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 hover:bg-gray-200'
            }`}
            style={category !== 'now_playing' ? { color: 'var(--color-text-primary)' } : {}}
          >
            Now Playing
          </button>
          <button
            onClick={() => setCategory('upcoming')}
            className={`px-4 py-2 rounded-lg text-sm transition ${
              category === 'upcoming'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 hover:bg-gray-200'
            }`}
            style={category !== 'upcoming' ? { color: 'var(--color-text-primary)' } : {}}
          >
            Upcoming
          </button>
          <button
            onClick={() => setCategory('trending')}
            className={`px-4 py-2 rounded-lg text-sm transition flex items-center gap-1 ${
              category === 'trending'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 hover:bg-gray-200'
            }`}
            style={category !== 'trending' ? { color: 'var(--color-text-primary)' } : {}}
          >
            <TrendingUp size={14} />
            Trending
          </button>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {movies.map((movie) => (
            <button
              key={movie.id}
              onClick={() => setSelectedMovie(movie)}
              className={`text-left group ${
                selectedMovie?.id === movie.id ? 'ring-2 ring-primary-500 rounded-lg' : ''
              }`}
            >
              <div className="relative mb-2 rounded-lg overflow-hidden">
                <img
                  src={imageErrors.has(movie.poster) ? getFallbackImage('poster') : movie.poster}
                  alt={movie.title}
                  className="w-full aspect-[2/3] object-cover group-hover:scale-105 transition duration-300"
                  onError={() => handleImageError(movie.poster)}
                />
                {movie.trending && (
                  <div className="absolute top-2 right-2 px-2 py-1 bg-red-600 text-white text-xs rounded-full flex items-center gap-1">
                    <TrendingUp size={10} />
                    Trending
                  </div>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition" />
              </div>
              <h3 className="font-medium text-sm mb-1 line-clamp-2" style={{ color: 'var(--color-text-primary)' }}>
                {movie.title}
              </h3>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <Star size={12} className="text-yellow-500 fill-current" />
                  <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{movie.rating}</span>
                </div>
                <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                  {new Date(movie.releaseDate).getFullYear()}
                </span>
              </div>
            </button>
          ))}
        </div>

        {movies.length === 0 && (
          <div className="text-center py-12" style={{ color: 'var(--color-text-secondary)' }}>
            <Film size={64} className="mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium mb-2">No Movies Found</p>
            <p className="text-sm">Try selecting a different category</p>
          </div>
        )}
      </div>

      {/* Movie Detail */}
      {selectedMovie && (
        <div className="w-96 overflow-y-auto border-l pl-4" style={{ borderColor: 'var(--color-border)' }}>
          <div className="relative mb-4 rounded-lg overflow-hidden">
            <img
              src={imageErrors.has(selectedMovie.backdrop) ? getFallbackImage('backdrop') : selectedMovie.backdrop}
              alt={selectedMovie.title}
              className="w-full h-48 object-cover"
              onError={() => handleImageError(selectedMovie.backdrop)}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <h2 className="text-xl font-bold text-white mb-2">{selectedMovie.title}</h2>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1">
                  <Star size={14} className="text-yellow-500 fill-current" />
                  <span className="text-white font-semibold">{selectedMovie.rating}</span>
                </div>
                {selectedMovie.trending && (
                  <div className="flex items-center gap-1 px-2 py-1 bg-red-600 text-white text-xs rounded-full">
                    <TrendingUp size={10} />
                    Trending
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>Release Date</h3>
              <div className="flex items-center gap-2" style={{ color: 'var(--color-text-secondary)' }}>
                <Calendar size={16} />
                {new Date(selectedMovie.releaseDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>Genres</h3>
              <div className="flex flex-wrap gap-2">
                {selectedMovie.genre.map((g) => (
                  <span
                    key={g}
                    className="px-3 py-1 text-sm rounded-full"
                    style={{ 
                      backgroundColor: 'var(--color-surface)', 
                      color: 'var(--color-text-primary)',
                      border: '1px solid var(--color-border)'
                    }}
                  >
                    {g}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>Overview</h3>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{selectedMovie.overview}</p>
            </div>

            <div className="flex gap-2">
              <button 
                onClick={() => handleWatchTrailer(selectedMovie)}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition flex items-center justify-center gap-2"
              >
                <Film size={16} />
                Watch Trailer
              </button>
              <button 
                className="px-4 py-2 rounded-lg transition"
                style={{ 
                  backgroundColor: 'var(--color-surface)', 
                  color: 'var(--color-text-primary)',
                  border: '1px solid var(--color-border)'
                }}
                title="Add to watchlist (coming soon)"
              >
                Watchlist
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-hidden">
        {isExpanded ? renderExpandedView() : renderCompactView()}
      </div>
    </div>
  )
}
